import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi, formatApiError } from "@/context/AuthContext";
import { LoadMore, SearchBox } from "@/components/site/ListControls";

// Generic list + create/edit dialog + delete confirmation for one admin resource
export const ResourcePanel = ({ resource, queryKey, items = [], label, renderRow, rowActions, Form, formProps = {}, list, detailPath }) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null closed, {} new, item edit
  const [toDelete, setToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: [queryKey] });

  // list endpoints omit the full text: load the complete item before editing
  const openEdit = async (it) => {
    if (!detailPath || !it.slug) return setEditing(it);
    try {
      setEditing({ ...(await adminApi.get(`${detailPath}/${it.slug}`)).data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async (payload) => {
    setSubmitting(true);
    try {
      if (editing?.id) await adminApi.put(`/admin/${resource}/${editing.id}`, payload);
      else await adminApi.post(`/admin/${resource}`, payload);
      toast.success("Enregistré.");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    try {
      await adminApi.delete(`/admin/${resource}/${toDelete.id}`);
      toast.success("Supprimé.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div data-testid={`admin-panel-${resource}`}>
      <div className="flex items-center justify-between mb-6">
        {list ? (
          <SearchBox value={list.q} onChange={list.setQ} placeholder="Rechercher…" testId={`admin-search-${resource}`} />
        ) : (
          <p className="text-zinc-400 text-sm">{items.length} élément(s)</p>
        )}
        <button onClick={() => setEditing({})} data-testid={`admin-add-${resource}`} className="inline-flex items-center gap-2 bg-primary text-white uppercase text-xs font-bold tracking-[0.2em] px-5 py-3 hover:bg-white hover:text-black transition-colors duration-200">
          <Plus className="w-4 h-4" /> Ajouter {label}
        </button>
      </div>
      <ul className="border-t border-white/10">
        {items.map((it) => (
          <li key={it.id} className="flex items-center gap-4 py-4 border-b border-white/10" data-testid={`admin-row-${resource}-${it.id}`}>
            <div className="flex-1 min-w-0">{renderRow(it)}</div>
            {rowActions?.(it)}
            <button onClick={() => openEdit(it)} aria-label="Modifier" data-testid={`admin-edit-${resource}-${it.id}`} className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-white transition-colors"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => setToDelete(it)} aria-label="Supprimer" data-testid={`admin-delete-${resource}-${it.id}`} className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-primary hover:text-primary transition-colors"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      {list && <LoadMore list={list} testId={`admin-load-more-${resource}`} />}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none bg-black border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`admin-dialog-${resource}`}>
          <DialogTitle className="font-display uppercase text-2xl text-white">{editing?.id ? "Modifier" : "Ajouter"} {label}</DialogTitle>
          {editing && <Form key={editing.id || "new"} initial={editing.id ? editing : undefined} onSubmit={save} submitting={submitting} {...formProps} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none bg-black border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase text-white">Supprimer ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete?.title || toDelete?.name} » sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="admin-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-none bg-primary hover:bg-white hover:text-black" data-testid="admin-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
